import * as Yup from "yup";
import { PhoneNumberUtil } from "google-libphonenumber";
import { TFunction } from "i18next";
import { BUSINESS_TYPE } from "../BusinessTypeSelector/businessType";
import { parseBusinessType } from "../BusinessTypeSelector/parseBusinessType";
import { BaseBranchFormValues, BranchFormValues, BuildInitialValuesOptions } from "./types";
import type { GetBranchQuery } from "@me-team/host/main/branches/graphql/branches.hooks";
import { mapBackendSchedulesToWeekValue } from "../Me-menu/WorkingHours/helpers/workingHoursHelpers";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string) => {
    try {
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch {
        return false;
    }
};

const getBaseBranchFormValidationShape = (t: TFunction) => {
    return {
        name: Yup.string()
            .max(50, t("Max length is", { number: 50 }))
            .required(t("Name is required")),
        country: Yup.number().required(t("Country is required")),
        city: Yup.number()
            .nullable()
            .test("city-required-if-no-customCity", t("City is required"), function (value) {
                const { customCity } = this.parent;
                return customCity ? true : value !== null && value !== undefined;
            })
            .test("customCity-error-location", "", function (value) {
                const { customCity } = this.parent;
                if (!customCity && (value === null || value === undefined)) {
                    return this.createError({
                        path: "customCity",
                        message: t("Custom city name is required"),
                    });
                }
                return true;
            }),
        customCity: Yup.string()
            .nullable()
            .test("customCity-required-if-no-city", t("Custom city name is required"), function (value) {
                const { city } = this.parent;
                return city !== -1 ? true : value !== null && value !== undefined && value !== "";
            }),
        address: Yup.string().required(t("Address is required")),
        phones: Yup.array()
            .min(1, t("At least one phone number is required"))
            .of(
                Yup.object().shape({
                    phone: Yup.string()
                        .required(t("Phone number is required"))
                        .test("is-valid-phone", t("Phone number is not valid"), (value) => isPhoneValid(value || "")),
                })
            ),
        website: Yup.string().nullable(),
        messageLanguage: Yup.string(),
        description: Yup.string()
            .max(200, t("Max length is", { number: 200 }))
            .test("not-empty-or-whitespace", t("Description cannot be empty or contain only spaces"), (value) =>
                value ? value.trim().length > 0 : true
            ),
    };
};

export const buildValidationSchemaByModules = (
    t: TFunction,
    { hasServiceExtraFields }: Pick<BuildInitialValuesOptions, "hasServiceExtraFields">
) => {
    return Yup.object().shape({
        ...getBaseBranchFormValidationShape(t),
        ...(hasServiceExtraFields
            ? {
                  workSchedule: Yup.string()
                      .max(50, t("Max length is", { number: 50 }))
                      .required(t("Work schedule is required")),
              }
            : {}),
    });
};

type GetBranchQueryBranch = NonNullable<
    NonNullable<NonNullable<NonNullable<GetBranchQuery["user"]>["company"]>["branchesPaginated"]>["branches"]
>[number];

type BranchWorkScheduleForInitialValues = NonNullable<
    NonNullable<GetBranchQueryBranch["generalWorkSchedules"]>[number]
>;

type BranchForInitialValues = Omit<GetBranchQueryBranch, "generalWorkSchedules" | "kitchenWorkSchedules"> & {
    generalWorkSchedules?: BranchWorkScheduleForInitialValues[] | null;
    kitchenWorkSchedules?: BranchWorkScheduleForInitialValues[] | null;
};

export const getBaseInitialValues = (currentBranch: BranchForInitialValues): BaseBranchFormValues => ({
    name: currentBranch.name,
    category: parseBusinessType(currentBranch.category?.type) || BUSINESS_TYPE.OTHER,
    type: currentBranch.type?.name,
    description: currentBranch.description || "",
    country: +currentBranch.country.id,
    city: +currentBranch.city!.id,
    address: currentBranch.address,
    phones: currentBranch.phones?.length ? currentBranch.phones : [{ phone: "" }],
    website: currentBranch.website || "",
    image: currentBranch.image || null,
    logo: null,
    instagram: currentBranch.socialLink?.instagram || "",
    facebook: currentBranch.socialLink?.facebook || "",
    youtube: currentBranch.socialLink?.youtube || "",
    whatsapp: currentBranch.socialLink?.whatsapp || "",
});

export const buildInitialValuesByModules = (
    currentBranch: BranchForInitialValues,
    { hasServiceExtraFields, hasFoodExtraFields }: BuildInitialValuesOptions
): BranchFormValues => {
    const base = getBaseInitialValues(currentBranch);

    return {
        ...base,
        ...(hasServiceExtraFields ? { workSchedule: currentBranch.workSchedule || "" } : null),
        ...(hasFoodExtraFields
            ? {
                  generalWorkingHours: mapBackendSchedulesToWeekValue(currentBranch.generalWorkSchedules || []),
                  kitchenWorkingHours: mapBackendSchedulesToWeekValue(currentBranch.kitchenWorkSchedules || []),
              }
            : null),
    };
};
